import { FC, useState, ChangeEvent } from 'react';
import { Button, Divider } from 'common-components';
import styles from './categoryForm.module.scss';
import { useTranslationContext } from '../../../contexts/translationContext';
import Toggle from '../../toggle/toggle';
import AdminInput from '../../input/input';
import UploadImage from '../uploadImage/uploadImage';

const CategoryForm: FC<CategoryFormProps> = (props: CategoryFormProps) => {
    const { onCancel, onSave, isEdit = false, categoryId, categoryName, categoryImage, isInternal = false } = props;
    const { translate } = useTranslationContext();

    const [isCurrentInternal, setCurrentInternal] = useState<boolean>(isInternal);
    const [currentCategoryName, setCurrentCategoryName] = useState<string | undefined>(categoryName);
    const [currentCategoryImage, setCurrentCategoryImage] = useState<string | undefined>(categoryImage);
    const [uploadedImage, setUploadedImage] = useState<File | undefined>(undefined);

    const onSaveHandler = () => {
        onSave({
            displayName: currentCategoryName || '',
            isInternal: isCurrentInternal,
            iconFile: uploadedImage,
            iconUrl: currentCategoryImage,
            ...(categoryId && { id: categoryId })
        });
    };

    const EDIT_HEADER = translate('EditCategory');
    const CREATE_HEADER = translate('CreateCategory');
    const CATEGORY_NAME = translate('CategoryName');
    const CATEGORY_IMAGE = translate('CategoryImage');
    const CREATE = translate('Create');
    const SAVE = translate('Save');
    const Close = translate('Close');
    const INTERNAL = translate('Internal');
    const TYPE_ANYTHING = translate('TypeAnything');

    const HEADER = isEdit ? EDIT_HEADER : CREATE_HEADER;
    const BUTTON_TEXT = isEdit ? SAVE : CREATE;

    const onUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (e.target.files) {
            setUploadedImage(e.target.files?.[0]);
            setCurrentCategoryImage(URL.createObjectURL(e.target.files?.[0]));
        }
    };

    const onDelete = (): void => {
        setCurrentCategoryImage(undefined);
    };

    const isFormValid = currentCategoryName;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>{HEADER}</div>
                <div className={styles['category-input-container']}>
                    <span className={styles['sub-header']}>{CATEGORY_NAME}</span>
                    <AdminInput
                        onChange={setCurrentCategoryName}
                        placeholder={TYPE_ANYTHING}
                        defaultValue={currentCategoryName}
                    />
                </div>
                <Divider orientation="vertical" className={styles.divider} color="rgba(98, 98, 98, 0.3)" />
                <div className={styles['category-image-container']}>
                    <div className={styles['input-container']}>
                        <span className={styles['sub-header']}>{CATEGORY_IMAGE}</span>
                        <UploadImage onUpload={onUpload} onDelete={onDelete} showDelete={!!currentCategoryImage} />
                    </div>
                    <div className={styles.displayer}>
                        {currentCategoryImage && (
                            <img src={currentCategoryImage} className={styles['displayer-image']} />
                        )}
                    </div>
                </div>
                <Divider orientation="vertical" className={styles.divider} color="rgba(98, 98, 98, 0.3)" />
                <div className={styles['internal-container']}>
                    <Toggle
                        defaultValue={isCurrentInternal}
                        onChange={(_event: any, checked: boolean) => setCurrentInternal(checked)}
                        size="small"
                    />
                    <span className={styles['sub-header']}>{INTERNAL}</span>
                </div>
            </div>
            <div className={styles.footer}>
                <Button onClick={onCancel} type="learningPanelAdminOnlyText" text={Close} lowerCase={true} />
                <Button
                    onClick={onSaveHandler}
                    type={isFormValid ? 'gradientNoBorder' : 'gradientNoBorderDisabled'}
                    text={BUTTON_TEXT}
                    lowerCase={true}
                />
            </div>
        </div>
    );
};

interface CategoryFormProps {
    isEdit?: boolean;
    onCancel: () => void;
    onSave: (category: {
        displayName: string;
        iconUrl: string | undefined;
        isInternal: boolean;
        id?: number;
        iconFile: File | undefined;
    }) => void;
    categoryName?: string;
    categoryImage?: string;
    isInternal?: boolean;
    categoryId?: number;
}

export default CategoryForm;
